import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import DemoRequestDialog from "@/components/landing/DemoRequestDialog";

// ultimatedzm.com is the product's marketing site and nothing else -- the app
// lives on each dropzone's own host. Its build sets this so the landing page
// stops offering a way in that would only bounce through a redirect.
// Unset everywhere else, so every other build keeps the buttons.
const MARKETING_ONLY = process.env.REACT_APP_MARKETING_ONLY === 'true';

const UDZMLanding = () => {
  const [lang, setLang] = useState("fr");
  const [modalOpen, setModalOpen] = useState(false);
  const [currentModal, setCurrentModal] = useState(null);
  // "Request a demo" (hero) and "Schedule a call" (CTA) both open this form.
  const [demoOpen, setDemoOpen] = useState(false);
  const [computerExpanded, setComputerExpanded] = useState(false);
  const [kioskIndex, setKioskIndex] = useState(0);
  const [terrainIndex, setTerrainIndex] = useState(0);
  const [aboutIndex, setAboutIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Simulator state — defaults match the on-screen example (6000 / $24k / 1200 / May–Oct)
  const [bookings, setBookings] = useState(6000);
  const [giftCards, setGiftCards] = useState(24000);
  const [videos, setVideos] = useState(1200);
  const [activeMonths, setActiveMonths] = useState([4, 5, 6, 7, 8, 9]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 860);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const content = {
    fr: {
      heroKicker: "Plus de sauts. Moins de chaos. De meilleures décisions.",
      modulesTitle: "LES 6 MODULES",
      modulesSubtitle: "UNE PLATEFORME. SIX LOGICIELS.",
      whyTitle: "GÉREZ VOTRE DROPZONE COMME UNE MACHINE.",
      aboutTitle: "QUI SOMMES-NOUS",
    },
    en: {
      heroKicker: "More jumps. Less chaos. Better decisions.",
      modulesTitle: "THE 6 MODULES",
      modulesSubtitle: "ONE PLATFORM. SIX TOOLS.",
      whyTitle: "RUN YOUR DROPZONE LIKE A MACHINE.",
      aboutTitle: "ABOUT US",
    },
  };

  const current = content[lang];

  const modules = [
    {
      icon: "✈️",
      num: "01",
      title: { fr: "Gestionnaire de Dropzone", en: "Dropzone Manager" },
      tag: { fr: "PLATEFORME", en: "PLATFORM" },
      tagClass: "bg-black text-white",
      image: "/Screenshot%202026-05-01%20at%2012.05.22%20PM.png",
      desc: {
        fr: "Gérez vos envolées, instructeurs et opérations depuis un tableau de bord centralisé. Multi-sites, temps réel.",
        en: "Manage loads, instructors and operations from a centralized dashboard. Multi-site, real-time.",
      },
    },
    {
      icon: "📋",
      num: "02",
      title: { fr: "Décharges de responsabilité", en: "Liability Waivers" },
      tag: { fr: "LÉGAL", en: "LEGAL" },
      tagClass: "border border-black text-black",
      image: "/Screenshot%202026-05-01%20at%2012.05.51%20PM.png",
      desc: {
        fr: "Signature électronique des décharges à la borne ou en ligne avant l'arrivée.",
        en: "Electronic waiver signing at the kiosk or online before arrival.",
      },
    },
    {
      icon: "💬",
      num: "03",
      title: { fr: "Messages texte (SMS)", en: "Text Messages (SMS)" },
      tag: { fr: "IA", en: "AI" },
      tagClass: "bg-red-600 text-white",
      image: "/Screenshot%202026-05-01%20at%2012.06.24%20PM.png",
      desc: {
        fr: "Confirmations, rappels et alertes envoyés automatiquement par SMS.",
        en: "Confirmations, reminders and alerts sent automatically via SMS.",
      },
    },
    {
      icon: "🎬",
      num: "04",
      title: { fr: "Vente & Montage Vidéo", en: "Video Sales & Editing" },
      tag: { fr: "IA", en: "AI" },
      tagClass: "bg-red-600 text-white",
      image: "/Screenshot%202026-05-01%20at%2012.07.00%20PM.png",
      desc: {
        fr: "Montage automatique et lien d'achat envoyé après chaque saut.",
        en: "Automatic editing and purchase link sent after every jump.",
      },
    },
    {
      icon: "📧",
      num: "05",
      title: { fr: "Gestion des courriels", en: "Email Management" },
      tag: { fr: "IA", en: "AI" },
      tagClass: "bg-red-600 text-white",
      image: "/Screenshot%202026-05-01%20at%2012.07.31%20PM.png",
      desc: {
        fr: "Tous les emails transactionnels et marketing automatisés.",
        en: "All transactional and marketing emails automated.",
      },
    },
    {
      icon: "📞",
      num: "06",
      title: { fr: "Agent IA au téléphone", en: "AI Phone Agent" },
      tag: { fr: "IA", en: "AI" },
      tagClass: "bg-red-600 text-white",
      image: "/Screenshot%202026-05-01%20at%2012.08.02%20PM.png",
      desc: {
        fr: "L'IA répond 24/7, qualifie et prend les réservations.",
        en: "AI answers calls 24/7, qualifies and takes bookings.",
      },
    },
  ];

  const openModal = (module) => {
    setCurrentModal(module);
    setModalOpen(true);
  };

  const kioskSlides = [
    "/first.png",
    "/second.png",
    "/third.png",
    "/fourth.png",
    "/fifth.png",
    "/sixth.png",
    "/seventh.png",
    "/eight.png",
    "/nine.png",
    "/tenth.png",
    "/eleventh.png",
  ];

  // Auto carousels
  useEffect(() => {
    const kioskTimer = setInterval(() => {
      setKioskIndex((prev) => (prev + 1) % kioskSlides.length);
    }, 4000);

    const terrainTimer = setInterval(() => {
      setTerrainIndex((prev) => (prev + 1) % 4);
    }, 4500);

    return () => {
      clearInterval(kioskTimer);
      clearInterval(terrainTimer);
    };
  }, [kioskSlides.length]);

  // ─── Pricing simulator ─────────────────────────────────────────────────────
  // Discount on bookings is tiered (marginal): every 2k bookings unlocks +10%
  // off that tier. At 8k+ the marginal rate caps at 40% — matching the
  // "Encore X réservations pour le rabais maximum de -40%" hint in the UI.
  const sim = useMemo(() => {
    const RATE_BOOKING = 1.5;
    const RATE_GIFT = 0.015;
    const RATE_VIDEO = 7.0;
    const TIERS = [
      { from: 0, to: 2000, rate: 0 },
      { from: 2000, to: 4000, rate: 0.1 },
      { from: 4000, to: 6000, rate: 0.2 },
      { from: 6000, to: 8000, rate: 0.3 },
      { from: 8000, to: Infinity, rate: 0.4 },
    ];

    const safeBookings = Math.max(0, Number(bookings) || 0);
    const safeGift = Math.max(0, Number(giftCards) || 0);
    const safeVideos = Math.max(0, Number(videos) || 0);

    const baseBookings = safeBookings * RATE_BOOKING;

    let discount = 0;
    for (const t of TIERS) {
      if (safeBookings <= t.from) break;
      const count = Math.min(safeBookings, t.to) - t.from;
      discount += count * RATE_BOOKING * t.rate;
    }
    const netBookings = baseBookings - discount;
    const avgDiscountPct =
      baseBookings > 0 ? Math.round((discount / baseBookings) * 100) : 0;

    const giftSubtotal = safeGift * RATE_GIFT;
    const videoSubtotal = safeVideos * RATE_VIDEO;

    const total = netBookings + giftSubtotal + videoSubtotal;
    const monthCount = activeMonths.length;
    const avgPerMonth = monthCount > 0 ? total / monthCount : 0;
    const remainingForMax = Math.max(0, 8000 - safeBookings);

    return {
      baseBookings,
      discount,
      netBookings,
      avgDiscountPct,
      giftSubtotal,
      videoSubtotal,
      total,
      monthCount,
      avgPerMonth,
      remainingForMax,
    };
  }, [bookings, giftCards, videos, activeMonths]);

  // French-style number formatting (space as thousands separator), no decimals.
  const fmt = (n) =>
    `$${Math.round(n).toLocaleString("en-US").replace(/,/g, " ")}`;
  const fmtNum = (n) =>
    Math.round(n).toLocaleString("en-US").replace(/,/g, " ");

  const monthNames =
    lang === "fr"
      ? ["JAN", "FÉV", "MAR", "AVR", "MAI", "JUN", "JUL", "AOÛ", "SEP", "OCT", "NOV", "DÉC"]
      : ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

  const toggleMonth = (idx) =>
    setActiveMonths((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx].sort((a, b) => a - b)
    );

  const seasonLabel = activeMonths.length
    ? activeMonths.map((i) => monthNames[i].charAt(0) + monthNames[i].slice(1).toLowerCase()).join(", ")
    : lang === "fr" ? "Aucun mois sélectionné" : "No months selected";

  return (
    <div className="min-h-screen bg-white text-[#0f0f0f] font-['DM_Sans'] overflow-x-hidden">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600&display=swap');
        .bebas { font-family: 'Bebas Neue', sans-serif; }
        html { scroll-behavior: smooth; scroll-padding-top: 4rem; }
        @media (prefers-reduced-motion: reduce) {
          html { scroll-behavior: auto; }
        }
      `}</style>

      {/* Masthead */}
      <div className="border-b-[1.5px] border-black px-6 py-6 md:px-10 md:py-8 grid grid-cols-1 md:grid-cols-[auto_1fr] items-start md:items-center gap-6">
        <img
          src="https://res.cloudinary.com/dxgvmldss/image/upload/v1773935934/Logo_UDZM_dr1kmx.png"
          alt="UDZM"
          loading="eager"
          decoding="async"
          className="h-16 md:h-20 w-auto object-contain"
        />
        <div className="flex flex-col items-start md:items-end gap-3 md:gap-4">
          <div className="flex flex-col items-start md:items-end gap-1.5">
            <div className="text-[10px] md:text-xs font-semibold tracking-[0.14em] uppercase text-[#888880]">
              Ultimate DZ Management
            </div>
            <div className="inline-flex items-center gap-2 border-[1.5px] border-red-600 text-red-600 px-3 py-1 text-[10px] md:text-xs font-semibold tracking-widest uppercase">
              <div className="w-1.5 h-1.5 bg-red-600 rounded-full animate-pulse" />
              {lang === "fr" ? "Propulsé par l'IA" : "AI-Powered"}
            </div>
          </div>
          {!MARKETING_ONLY && (
          <div className="flex flex-wrap items-stretch gap-3">
            {/* Buy a gift card — public, no login (see /gift-cards). Inside the
                MARKETING_ONLY guard on purpose: gift cards belong to a
                dropzone, so the product marketing host must not sell them. */}
            <Link
              to="/gift-cards"
              className="border-[1.5px] border-black hover:bg-black hover:text-white px-5 md:px-7 py-3 text-[11px] md:text-xs font-semibold tracking-[0.14em] uppercase transition-all"
            >
              {lang === "fr" ? "Carte-cadeau" : "Buy Gift Card"}
            </Link>
            <Link
              to="/login?type=customer"
              className="border-[1.5px] border-black hover:bg-black hover:text-white px-5 md:px-7 py-3 text-[11px] md:text-xs font-semibold tracking-[0.14em] uppercase transition-all"
            >
              {lang === "fr" ? "Connexion Client" : "Customer Login"}
            </Link>
            {/* Bug 6 — the "Dropzone/Staff" button must open the STAFF login.
                Both buttons previously pointed at bare "/login", which
                LoginPage defaults to type=customer, so this looped back to the
                customer view. Pass the explicit type so it lands on staff. */}
            <Link
              to="/login?type=staff"
              className="bg-red-600 hover:bg-red-700 text-white border-[1.5px] border-red-600 px-5 md:px-7 py-3 text-[11px] md:text-xs font-semibold tracking-[0.14em] uppercase transition-all"
            >
              {lang === "fr" ? "Connexion Dropzone" : "Dropzone Login"}
            </Link>
          </div>
          )}
        </div>
      </div>

      {/* Sticky Navigation */}
      <nav className="sticky top-0 z-50 bg-black text-white h-12 md:h-14 flex items-center px-6 md:px-10 border-b border-black">
        <div className="flex-1 flex items-center gap-4 md:gap-8 text-[10px] md:text-xs font-semibold tracking-[0.12em] uppercase overflow-x-auto pb-1">
          <a href="#modules" className="hover:text-white whitespace-nowrap">
            {lang === "fr" ? "Modules" : "Modules"}
          </a>
          <a href="#kiosk" className="hover:text-white whitespace-nowrap">
            {lang === "fr" ? "Borne" : "Kiosk"}
          </a>
          <a href="#photos" className="hover:text-white whitespace-nowrap">
            {lang === "fr" ? "Terrain" : "In Action"}
          </a>
          <a href="#why" className="hover:text-white whitespace-nowrap">
            {lang === "fr" ? "Pourquoi UDZM" : "Why UDZM"}
          </a>
          <a href="#about" className="hover:text-white whitespace-nowrap">
            {lang === "fr" ? "Qui sommes-nous" : "About Us"}
          </a>
          <a href="#simulator" className="hover:text-white whitespace-nowrap">
            {lang === "fr" ? "Simulateur" : "Simulator"}
          </a>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-2 text-xs font-semibold tracking-widest border border-red-600/50 text-red-600 px-3 py-1 rounded">
            <div className="w-1.5 h-1.5 bg-red-600 rounded-full animate-pulse" />
            {lang === "fr" ? "Agent IA ACTIF" : "Active AI AGENT"}
          </div>

          {/* Language Toggle */}
          <div className="flex border border-white/30 text-xs font-semibold overflow-hidden rounded">
            <button
              onClick={() => setLang("fr")}
              className={`px-4 py-1 transition-all ${lang === "fr" ? "bg-red-600 text-white" : "text-white/70 hover:text-white"}`}
            >
              FR
            </button>
            <button
              onClick={() => setLang("en")}
              className={`px-4 py-1 transition-all ${lang === "en" ? "bg-red-600 text-white" : "text-white/70 hover:text-white"}`}
            >
              EN
            </button>
          </div>
        </div>
      </nav>

      {/* HERO SECTION */}
      <section className="border-b-[1.5px] border-black">
        <div className="border-b-[1.5px] border-black min-h-[calc(100vh-3.5rem)] flex">
          <div className="relative w-full p-8 md:p-14 lg:px-20 lg:py-24 flex flex-col justify-center">
            {/* Red accent bar */}
            <div className="absolute top-0 left-0 w-1 h-full bg-red-600" />

            <div className="text-red-600 text-xs md:text-sm font-semibold tracking-[0.15em] uppercase mb-6">
              {current.heroKicker}
            </div>

            <h1 className="bebas text-[2.5rem] sm:text-[3.25rem] md:text-[4.25rem] lg:text-[5rem] leading-[0.95] tracking-[0.02em] mb-8">
              {lang === "fr" ? (
                <>
                  LE SYSTÈME<br />
                  D'EXPLOITATION IA<br />
                  <span className="text-red-600">DES DROPZONES<br />MODERNES.</span>
                </>
              ) : (
                <>
                  THE AI OPERATING<br />
                  SYSTEM FOR<br />
                  <span className="text-red-600">MODERN<br />DROPZONES.</span>
                </>
              )}
            </h1>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-6 h-0.5 bg-red-600" />
              <span className="text-xs md:text-sm font-semibold tracking-[0.12em] uppercase text-[#2a2a2a]">
                Ultimate DropZone Management
              </span>
            </div>

            <p className="text-sm md:text-base text-[#2a2a2a] font-light max-w-md leading-[1.7] mb-10">
              {lang === "fr" ? (
                <>UDZM n'est pas juste un autre logiciel de dropzone. C'est la plateforme IA qui connecte <strong className="font-medium text-[#0f0f0f]">chaque réservation, chaque saut, chaque client</strong> — du premier appel jusqu'au centième saut.</>
              ) : (
                <>UDZM isn't just another dropzone software. It's the AI-powered platform that connects <strong className="font-medium text-[#0f0f0f]">every booking, every jump, every customer</strong> — from the first call to the hundredth jump.</>
              )}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                onClick={() => setDemoOpen(true)}
                className="bg-red-600 hover:bg-[#a81e1e] text-white border-[1.5px] border-red-600 hover:border-[#a81e1e] px-8 py-3 font-semibold tracking-[0.06em] text-sm uppercase transition-all hover:-translate-y-0.5">
                {lang === "fr" ? "Demander une démo" : "Request a demo"}
              </button>
              <a
                href="#modules"
                className="border-[1.5px] border-black hover:bg-black hover:text-white px-8 py-3 font-medium tracking-[0.06em] text-sm uppercase transition-all text-center hover:-translate-y-0.5"
              >
                {lang === "fr" ? "Voir les modules" : "See modules"}
              </a>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4">
          <div className="px-8 py-5 md:px-10 md:py-6 border-r-[1.5px] border-black flex flex-col gap-1">
            <div className="bebas text-3xl md:text-[1.85rem] tracking-[0.04em] leading-none text-red-600">6</div>
            <div className="text-[10px] md:text-[0.68rem] uppercase tracking-[0.05em] text-[#888880]">
              {lang === "fr" ? "Logiciels en 1" : "Tools in 1"}
            </div>
          </div>
          <div className="px-8 py-5 md:px-10 md:py-6 lg:border-r-[1.5px] border-black flex flex-col gap-1">
            <div className="bebas text-3xl md:text-[1.85rem] tracking-[0.04em] leading-none">
              -40<span className="text-[1.1rem]">%</span>
            </div>
            <div className="text-[10px] md:text-[0.68rem] uppercase tracking-[0.05em] text-[#888880]">
              {lang === "fr" ? "Jusqu'à 40% moins cher" : "Up to 40% cheaper"}
            </div>
          </div>
          <div className="px-8 py-5 md:px-10 md:py-6 border-r-[1.5px] border-black border-t-[1.5px] lg:border-t-0 flex flex-col gap-1">
            <div className="bebas text-3xl md:text-[1.85rem] tracking-[0.04em] leading-none text-red-600">
              100<span className="text-[1.1rem]">%</span>
            </div>
            <div className="text-[10px] md:text-[0.68rem] uppercase tracking-[0.05em] text-[#888880]">
              {lang === "fr" ? "Autonome" : "Autonomous"}
            </div>
          </div>
          <div className="px-8 py-5 md:px-10 md:py-6 border-t-[1.5px] lg:border-t-0 border-black flex flex-col gap-1">
            <div className="bebas text-3xl md:text-[1.85rem] tracking-[0.04em] leading-none">24/7</div>
            <div className="text-[10px] md:text-[0.68rem] uppercase tracking-[0.05em] text-[#888880]">
              {lang === "fr" ? "Agent IA actif" : "AI Agent Active"}
            </div>
          </div>
        </div>
      </section>

      {/* MODULES SECTION */}
      <section id="modules" className="border-b border-black">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] border-b border-black">
          <div className="p-10 md:p-16 border-r border-black">
            <div className="text-red-600 text-xs font-semibold tracking-widest uppercase mb-4">
              {current.modulesTitle}
            </div>
            <div className="bebas text-3xl md:text-4xl leading-none">
              {current.modulesSubtitle}
            </div>
          </div>
          <div className="p-10 md:p-16 text-[#2a2a2a]">
            {lang === 'fr' ? (
              <>
                <strong>Pas juste un logiciel. Une dropzone plus intelligente. </strong>
                La plupart des plateformes gèrent les réservations et les décharges. UDZM va plus loin — <span className="text-red-600">une couche intelligente qui augmente le volume de sauts, réduit le chaos opérationnel, améliore la sécurité et fidélise vos clients.</span>
              </>
            ) : (
              <>
                <strong>Not just software. A smarter dropzone. </strong>
                Most platforms handle bookings and drop-offs. UDZM goes further — <span className="text-red-600">an intelligent layer that increases jump volume, reduces operational chaos, enhances safety, and retains your customers.</span>
              </>
            )}
          </div>
        </div>

        {/* Interactive Computer */}
        <div
          className="p-8 md:p-16 bg-[#f5f3f0] border-t border-black min-h-[380px] flex flex-col items-center justify-center relative cursor-pointer"
          onMouseEnter={() => !isMobile && setComputerExpanded(true)}
          onMouseLeave={() => !isMobile && setComputerExpanded(false)}
          onClick={() => isMobile && setComputerExpanded(!computerExpanded)}
        >
          <div className="relative w-full max-w-3xl">
            <motion.div
              className={`transition-all duration-500 ${computerExpanded ? "opacity-0 scale-95 pointer-events-none" : "opacity-100"}`}
              initial={{ opacity: 1 }}
            >
              <svg
                viewBox="0 0 520 380"
                className="w-full max-w-[520px] mx-auto drop-shadow-2xl"
              >
                <rect x="40" y="20" width="440" height="280" rx="12" fill="#0f0f0f" />
                <rect x="52" y="32" width="416" height="256" rx="6" fill="#1a1a1a" />
                <text x="260" y="135" textAnchor="middle" fill="#ffffff" fontSize="58" fontFamily="'Bebas Neue'" letterSpacing="4">
                  UDZM
                </text>
                <text x="260" y="165" textAnchor="middle" fill="#888880" fontSize="12" letterSpacing="3">
                  ULTIMATE DZ MANAGEMENT
                </text>
                <rect x="185" y="180" width="150" height="3" fill="#d92b2b" />
                <text x="260" y="215" textAnchor="middle" fill="#d92b2b" fontSize="15" fontWeight="700" className=" animate-pulse" letterSpacing="3">
                  {lang === "fr" ? "CLIQUEZ POUR EXPLORER" : "CLICK TO EXPLORE"}
                </text>
                <text x="260" y="240" textAnchor="middle" fill="#d92b2b" fontSize="20" fontWeight="700">↓</text>
                <rect x="200" y="300" width="120" height="12" fill="#0f0f0f" />
                <rect x="180" y="312" width="160" height="6" rx="3" fill="#0f0f0f" />
              </svg>
            </motion.div>

            {/* Module Cards */}
            <div
              className={`grid grid-cols-2 md:grid-cols-3 gap-4 absolute inset-0 transition-all duration-500 ${computerExpanded ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"}`}
            >
              {modules.map((mod, i) => (
                <div
                  key={i}
                  onClick={() => openModal(mod)}
                  className="bg-white border-2 border-black p-6 flex flex-col items-center justify-center hover:bg-red-600 hover:text-white transition-all duration-200 cursor-pointer group"
                >
                  <div className="text-4xl mb-4 group-hover:text-white transition-colors">
                    {mod.icon}
                  </div>
                  <div className="bebas text-red-600 text-lg tracking-widest">
                    {mod.num}
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-center mt-2 group-hover:text-white">
                    {mod.title[lang]}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {computerExpanded && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setComputerExpanded(false);
              }}
              className="mt-8 text-sm font-semibold tracking-widest text-[#888880] hover:text-black flex items-center gap-2"
            >
              ← {lang === "fr" ? "Retour à l'aperçu" : "Back to overview"}
            </button>
          )}
        </div>
      </section>

      {/* KIOSK SECTION */}
      <section
        id="kiosk"
        className="grid grid-cols-1 lg:grid-cols-2"
      >
        <div className="p-10 md:p-16 flex flex-col justify-center">
          <div className="text-red-600 text-xs font-semibold tracking-widest uppercase mb-6">
            {lang === "fr" ? "Accueil autonome" : "Autonomous Check-in"}
          </div>
          <div className="bebas text-3xl md:text-4xl leading-none mb-10">
            {lang === "fr" ? (
              <>BORNE D'ENREGISTREMENT<br /><span className="text-red-600">AUTONOME</span></>
            ) : (
              <>AUTONOMOUS <br />CHECK-IN <br /><span className="text-red-600">KIOSK</span></>
            )}
          </div>
          <p className="text-sm md:text-base text-[#2a2a2a] max-w-md leading-relaxed mb-10">
            {lang === "fr" ? (
              <>L'accueil des clients se fait <strong className="font-semibold">sans aucune intervention humaine</strong>. La borne guide le client de l'arrivée jusqu'au saut : identification, signature de décharge, paiement et assignation d'envolée.</>
            ) : (
              <>Client check-in happens without any human involvement. The kiosk guides the client from arrival to jump: identification, waiver signing, payment, and load assignment.</>
            )}
          </p>
          <ul className="flex flex-col gap-3 max-w-md">
            {(lang === "fr"
              ? [
                  "Identification du client (nom, réservation)",
                  "Signature électronique de la décharge",
                  "Paiement intégré (carte, mobile)",
                  "SMS de confirmation avec détails de l'envolée",
                  "Assignation automatique à l'envolée",
                ]
              : [
                  "Client identification (name, booking)",
                  "Electronic waiver signing",
                  "Integrated payment (card, mobile)",
                  "Confirmation SMS with load details",
                  "Automatic load assignment",
                ]
            ).map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-sm md:text-base text-[#2a2a2a]">
                <span className="text-red-600 font-bold mt-0.5">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Kiosk Right — full bleed auto-rotating carousel */}
        <div className="relative overflow-hidden bg-[#f5f3f0]" style={{ minHeight: 560 }}>
          <div
            className="flex h-full transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${kioskIndex * 100}%)`, height: '100%' }}
          >
            {kioskSlides.map((src, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-full h-full flex items-center justify-center p-6 md:p-10"
                style={{ minHeight: 560 }}
              >
                <img
                  src={src}
                  alt={`Kiosk view ${i + 1}`}
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                  className="max-w-full max-h-full object-contain"
                  style={{ clipPath: "inset(2% 6% 2% 6%)" }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PHOTO CAROUSEL — UDZM IN THE FIELD */}
      <section id="photos" className="border-b border-black">
        <div className="flex items-center justify-between px-8 md:px-12 py-5 border-b border-black">
          <div className="flex items-center gap-4">
            <div className="text-red-600 text-xs font-semibold tracking-widest uppercase">
              {lang === "fr" ? "EN ACTION" : "IN ACTION"}
            </div>
            <div className="bebas text-2xl tracking-wide">
              {lang === "fr" ? "UDZM SUR LE TERRAIN" : "UDZM IN THE FIELD"}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setTerrainIndex((prev) => (prev - 1 + 4) % 4)}
              className="w-9 h-9 border-[1.5px] border-black bg-transparent flex items-center justify-center hover:bg-black hover:text-white transition-all text-sm"
            >
              ←
            </button>
            <button
              onClick={() => setTerrainIndex((prev) => (prev + 1) % 4)}
              className="w-9 h-9 border-[1.5px] border-black bg-black text-white flex items-center justify-center hover:bg-red-600 hover:border-red-600 transition-all text-sm"
            >
              →
            </button>
          </div>
        </div>

        <div className="overflow-hidden bg-[#f5f3f0]">
          <div
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${terrainIndex * 100}%)` }}
          >
            {[
              {
                src: "/udzm1.png",
                label: lang === "fr" ? "Interface d'accueil" : "Welcome interface",
              },
              {
                src: "/udzm2.png",
                label: lang === "fr" ? "Tableau de bord" : "Dashboard",
              },
              {
                src: "/udzm3.png",
                label: lang === "fr" ? "Gestion des envolées" : "Load management",
              },
              {
                src: "/udzm4.png",
                label: lang === "fr" ? "Vue opérationnelle" : "Operational view",
              },
            ].map((slide, i) => (
              <div key={i} className="w-full flex-shrink-0 relative">
                <img
                  src={slide.src}
                  alt={slide.label}
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                  className="w-full h-auto block"
                />
                <div className="absolute bottom-4 left-6 md:left-12 text-xs font-medium text-white tracking-widest uppercase bg-black/60 backdrop-blur-sm px-3 py-1.5">
                  {slide.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-2 justify-center py-6">
          {[0, 1, 2, 3].map((i) => (
            <button
              key={i}
              onClick={() => setTerrainIndex(i)}
              className={`w-2 h-2 rounded-full transition-all ${terrainIndex === i ? "bg-red-600" : "bg-[#d4d2ce]"}`}
            />
          ))}
        </div>
      </section>

      {/* WHY UDZM */}
      <section id="why" className="grid grid-cols-1 lg:grid-cols-2 border-b border-black">
        <div className="p-10 md:p-16 lg:border-r border-b lg:border-b-0 border-black flex flex-col justify-center">
          <div className="text-red-600 text-xs font-semibold tracking-widest uppercase mb-4">
            {lang === "fr" ? "CE QUE ÇA CHANGE" : "WHAT THIS MEANS"}
          </div>
          <div className="bebas text-3xl md:text-4xl leading-none mb-8">
            {lang === "fr" ? (
              <>GÉREZ VOTRE<br />DROPZONE<br /><span className="text-red-600">COMME UNE<br />MACHINE.</span></>
            ) : (
              <>RUN YOUR<br />DROPZONE<br /><span className="text-red-600">LIKE A<br />MACHINE.</span></>
            )}
          </div>
          <p className="text-sm font-light text-[#2a2a2a] leading-relaxed max-w-sm">
            {lang === "fr"
              ? <>Moins de stress à l'accueil. Plus de sauts. UDZM permet à votre équipe de se concentrer sur ce qui compte vraiment : <strong className="font-semibold text-black">faire voler des avions, offrir des expériences inoubliables, et faire grandir le sport.</strong></>
              : <>Less front-desk stress. More jumps out the door. UDZM helps your team focus on what matters most: <strong className="font-semibold text-black">flying airplanes, delivering unforgettable experiences, and growing the sport.</strong></>
            }
          </p>
        </div>

        <div className="p-10 md:p-16 flex flex-col divide-y divide-[#e8e6e2]">
          {[
            {
              fr: <><strong className="font-semibold text-black">Optimisation des envolées.</strong> Une planification plus intelligente et l'optimisation des envolées rendent les avions mieux remplis!</>,
              en: <><strong className="font-semibold text-black">Load optimization.</strong> Smarter scheduling and load optimization keep aircraft flying full.</>,
            },
            {
              fr: <><strong className="font-semibold text-black">Enregistrement ultra-rapide.</strong> La préparation et la vérification automatisées éliminent les files d'attente à l'accueil.</>,
              en: <><strong className="font-semibold text-black">Faster check-in.</strong> Automated preparation and verification reduce front-desk bottlenecks.</>,
            },
            {
              fr: <><strong className="font-semibold text-black">Moins de no-shows.</strong> Les rappels intelligents et la reprogrammation tenant compte de la météo maintiennent l'engagement des clients.</>,
              en: <><strong className="font-semibold text-black">Fewer no-shows.</strong> Smart reminders and weather-aware rebooking keep customers committed.</>,
            },
            {
              fr: <><strong className="font-semibold text-black">Plus de revenus par visiteur.</strong> Les ventes incitatives de vidéos, coaching, formations et événements se font automatiquement.</>,
              en: <><strong className="font-semibold text-black">Higher revenue per visitor.</strong> Upsells for video, coaching, training, and events happen automatically.</>,
            },
            {
              fr: <><strong className="font-semibold text-black">Meilleure visibilité opérationnelle.</strong> Réservations, météo, personnel, sécurité — tout en un coup d'œil, en temps réel.</>,
              en: <><strong className="font-semibold text-black">Better operational awareness.</strong> Bookings, weather, staff, and safety — all visible instantly, in one place.</>,
            },
            {
              fr: <><strong className="font-semibold text-black">Construite pour la prochaine génération de dropzones.</strong> Plus de réservations, plus de clients, plus de contrôles de sécurité — UDZM rassemble tout en un seul système intelligent.</>,
              en: <><strong className="font-semibold text-black">Built for the next generation of dropzones.</strong> More bookings, more customers, more safety checks — UDZM brings everything together into one intelligent system.</>,
            },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-4 py-4">
              <div className="w-1.5 h-1.5 rounded-full bg-red-600 flex-shrink-0 mt-2" />
              <p className="text-sm font-light text-[#2a2a2a] leading-relaxed">
                {lang === "fr" ? item.fr : item.en}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] border-b border-black">
        <div className="p-10 md:p-14 border-b lg:border-b-0 lg:border-r border-black flex flex-col justify-center">
          <div className="text-red-600 text-xs font-semibold tracking-widest uppercase mb-4">
            {lang === "fr" ? "QUI SOMMES-NOUS" : "ABOUT US"}
          </div>
          <div className="bebas text-3xl md:text-4xl leading-none">
            {lang === "fr" ? (
              <>L'ÉQUIPE<br />DERRIÈRE<br /><span className="text-red-600">UDZM.</span></>
            ) : (
              <>THE TEAM<br />BEHIND<br /><span className="text-red-600">UDZM.</span></>
            )}
          </div>
        </div>
        <div className="bg-[#f5f3f0] p-10 md:p-14 flex flex-col justify-between gap-8">
          {(() => {
            const founders = [
              {
                initials: "GP",
                name: "Gregory Perrimond",
                avatarBg: "bg-red-600",
                accent: "text-red-600",
                accentBar: "bg-red-600",
                role: { fr: "Co-fondateur & CEO", en: "Co-founder & CEO" },
                body: {
                  fr: (
                    <>
                      <p>
                        Depuis plus de 15 ans, je gère et fais évoluer une dropzone — confronté directement aux vrais défis de l'industrie : marges serrées, opérations complexes et une multitude d'outils déconnectés.
                      </p>
                      <p>
                        Je suis Gregory Perrimond, et comme la plupart des propriétaires de dropzone, j'en ai eu assez de jongler avec plusieurs logiciels coûteux qui ne fonctionnaient pas ensemble. Alors j'ai construit ma propre solution.
                      </p>
                      <p>
                        <strong className="font-semibold">UDZM est la plateforme que j'aurais voulu avoir dès le premier jour.</strong> Un vrai système 6-en-1, conçu à partir d'une expérience opérationnelle réelle. Fait pour aider les propriétaires à réduire les dépenses inutiles, automatiser les opérations et offrir une expérience client irréprochable.
                      </p>
                      <p className="text-red-600 font-medium">
                        Ce n'est pas juste un logiciel — c'est une meilleure façon de gérer une dropzone.
                      </p>
                    </>
                  ),
                  en: (
                    <>
                      <p>
                        For over 15 years I've been running and growing a dropzone — facing the industry's real challenges head-on: thin margins, complex operations, and a patchwork of disconnected tools.
                      </p>
                      <p>
                        I'm Gregory Perrimond, and like most dropzone owners, I'd had enough of juggling expensive software that didn't talk to each other. So I built my own solution.
                      </p>
                      <p>
                        <strong className="font-semibold">UDZM is the platform I wish I'd had on day one.</strong> A true 6-in-1 system, designed from real operational experience. Built to help owners cut wasted spend, automate operations and deliver a flawless customer experience.
                      </p>
                      <p className="text-red-600 font-medium">
                        It's not just software — it's a better way to run a dropzone.
                      </p>
                    </>
                  ),
                },
              },
              {
                initials: "CRL",
                name: "Chantale Roch-Lachapelle",
                avatarBg: "bg-black",
                accent: "text-red-600",
                accentBar: "bg-black",
                role: { fr: "Co-fondatrice & COO", en: "Co-founder & COO" },
                body: {
                  fr: (
                    <>
                      <p>
                        Dans le monde du parachutisme depuis plus de 13 ans, j'ai évolué à travers pratiquement tous les rôles au sol : du manifeste à la gestion, en passant par l'administration. Cette immersion complète m'a donné une compréhension concrète et sans filtre des réalités du terrain.
                      </p>
                      <p>
                        Rapidement, une chose est devenue évidente : il manquait des outils adaptés, efficaces et réellement pensés pour ceux qui font vivre l'industrie au quotidien.
                      </p>
                      <p>
                        <strong className="font-semibold">C'est de là qu'est née UDZM.</strong>
                      </p>
                      <p className="text-red-600 font-medium">
                        Une solution conçue par le terrain, pour le terrain — qui regroupe enfin tous les outils, ajoute ce qui manquait, simplifie les opérations et répond aux vrais besoins des centres de parachutisme.
                      </p>
                    </>
                  ),
                  en: (
                    <>
                      <p>
                        In the skydiving world for more than 13 years, I've worked through nearly every ground-side role: from manifest to management to administration. That full-stack immersion gave me an unfiltered, concrete understanding of how a dropzone actually runs.
                      </p>
                      <p>
                        It quickly became obvious: the industry was missing tools that were genuinely fit-for-purpose, efficient, and built for the people running operations every day.
                      </p>
                      <p>
                        <strong className="font-semibold">That's where UDZM was born.</strong>
                      </p>
                      <p className="text-red-600 font-medium">
                        A solution built by the field, for the field — finally bringing every tool together, adding what was missing, simplifying operations and answering the real needs of skydiving centers.
                      </p>
                    </>
                  ),
                },
              },
              {
                initials: "JPL",
                name: "Jean-Philippe Lemire",
                avatarBg: "bg-[#1e3a8a]",
                accent: "text-[#1e3a8a]",
                accentBar: "bg-[#1e3a8a]",
                role: {
                  fr: "Co-fondateur — MetaCognixion",
                  en: "Co-founder — MetaCognixion",
                },
                body: {
                  fr: (
                    <>
                      <p>
                        PDG du studio visionnaire derrière l'architecture IA et technologique qui propulse UDZM, Jean-Philippe dirige la transformation des opérations d'entreprise en systèmes autonomes et axés sur les données. Avec des décennies d'expérience en logiciels, IA et optimisation opérationnelle, il se concentre sur la création de plateformes évolutives qui éliminent la complexité et libèrent la performance.
                      </p>
                      <p className="text-red-600 font-medium">
                        Propulsé par MetaCognixion — un studio d'ingénierie IA d'élite qui conçoit des systèmes opérationnels de nouvelle génération.
                      </p>
                    </>
                  ),
                  en: (
                    <>
                      <p>
                        CEO of the visionary studio behind the AI and technology architecture that powers UDZM, Jean-Philippe leads the transformation of enterprise operations into autonomous, data-driven systems. With decades of experience across software, AI and operational optimization, he focuses on building scalable platforms that strip out complexity and unlock performance.
                      </p>
                      <p className="text-red-600 font-medium">
                        Powered by MetaCognixion — an elite AI engineering studio designing the next generation of operational systems.
                      </p>
                    </>
                  ),
                },
              },
            ];
            const founder = founders[aboutIndex];
            return (
              <>
                <div className="flex flex-col gap-6">
                  <div className="flex items-center gap-5">
                    <div className={`w-14 h-14 rounded-full ${founder.avatarBg} text-white flex items-center justify-center bebas text-xl tracking-widest flex-shrink-0`}>
                      {founder.initials}
                    </div>
                    <div>
                      <div className="bebas text-xl md:text-2xl leading-none tracking-wide">
                        {founder.name}
                      </div>
                      <div className={`${founder.accent} text-xs md:text-sm font-semibold tracking-[0.18em] uppercase mt-2`}>
                        {founder.role[lang]}
                      </div>
                    </div>
                  </div>
                  <div className={`w-12 h-px ${founder.accentBar}`} />
                  <div className="flex flex-col gap-4 text-sm text-[#2a2a2a] leading-relaxed max-w-2xl">
                    {founder.body[lang]}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-4 pt-4 border-t border-[#e8e6e2]">
                  <div className="flex gap-2">
                    {founders.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setAboutIndex(i)}
                        className={`w-2 h-2 rounded-full transition-all ${aboutIndex === i ? "bg-red-600" : "bg-[#d4d2ce]"}`}
                        aria-label={`Founder ${i + 1}`}
                      />
                    ))}
                  </div>
                  <div className="text-xs font-semibold tracking-widest text-[#888880]">
                    {aboutIndex + 1} / {founders.length}
                  </div>
                  <button
                    onClick={() => setAboutIndex((prev) => (prev - 1 + founders.length) % founders.length)}
                    className="w-9 h-9 border-[1.5px] border-black bg-transparent flex items-center justify-center hover:bg-black hover:text-white transition-all text-sm"
                    aria-label="Previous founder"
                  >
                    ←
                  </button>
                  <button
                    onClick={() => setAboutIndex((prev) => (prev + 1) % founders.length)}
                    className="w-9 h-9 border-[1.5px] border-black bg-black text-white flex items-center justify-center hover:bg-red-600 hover:border-red-600 transition-all text-sm"
                    aria-label="Next founder"
                  >
                    →
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* SIMULATEUR DE PRIX */}
      <section id="simulator" className="border-b border-black">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] border-b border-black">
          <div className="p-10 md:p-14 border-b lg:border-b-0 lg:border-r border-black flex flex-col justify-center">
            <div className="text-red-600 text-xs font-semibold tracking-widest uppercase mb-3">
              {lang === "fr" ? "Outil interactif" : "Interactive tool"}
            </div>
            <div className="flex items-baseline gap-5">
              <div className="bebas text-4xl md:text-5xl text-[#d4d2ce] leading-none">05</div>
              <div className="bebas text-2xl md:text-3xl leading-none">
                {lang === "fr" ? <>SIMULATEUR<br />DE PRIX</> : <>PRICING<br />SIMULATOR</>}
              </div>
            </div>
          </div>
          <div className="p-10 md:p-14 text-[#2a2a2a] flex items-center">
            <p className="text-sm md:text-base leading-relaxed max-w-xl">
              {lang === "fr" ? (
                <>Estimez le coût annuel d'UDZM pour votre dropzone en quelques secondes. Entrez votre volume annuel, la valeur des gift cards, les vidéos et vos <strong className="font-semibold">mois d'opération</strong> — le calcul se fait instantanément.</>
              ) : (
                <>Estimate UDZM's annual cost for your dropzone in seconds. Enter your yearly volume, gift card value, videos and <strong className="font-semibold">months in operation</strong> — the calculation runs instantly.</>
              )}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Inputs */}
          <div className="p-8 md:p-12 border-b lg:border-b-0 lg:border-r border-black border-l-[3px] border-l-red-600">
            <div className="text-[10px] md:text-xs font-semibold tracking-[0.18em] uppercase text-[#888880] mb-8">
              {lang === "fr" ? "Vos données annuelles" : "Your annual data"}
            </div>

            {/* Bookings input */}
            <label className="block mb-8">
              <div className="text-[10px] md:text-xs font-semibold tracking-[0.18em] uppercase text-[#0f0f0f] mb-3">
                {lang === "fr" ? "Réservations / an" : "Bookings / year"}
              </div>
              <div className="grid grid-cols-[1fr_auto] border-[1.5px] border-black">
                <input
                  type="number"
                  min="0"
                  value={bookings}
                  onChange={(e) => setBookings(Math.max(0, parseInt(e.target.value || "0", 10)))}
                  className="px-4 py-3 text-lg bebas tracking-wider focus:outline-none"
                />
                <div className="px-4 py-3 bg-[#f5f3f0] text-xs tracking-widest uppercase text-[#888880] flex items-center border-l border-black">
                  {lang === "fr" ? "réservations" : "bookings"}
                </div>
              </div>
            </label>

            {/* Gift cards input */}
            <label className="block mb-8">
              <div className="text-[10px] md:text-xs font-semibold tracking-[0.18em] uppercase text-[#0f0f0f] mb-3">
                {lang === "fr" ? "Valeur totale gift cards / an" : "Total gift card value / year"}
              </div>
              <div className="grid grid-cols-[auto_1fr_auto] border-[1.5px] border-black">
                <div className="px-4 py-3 bg-[#f5f3f0] text-base font-semibold border-r border-black">$</div>
                <input
                  type="number"
                  min="0"
                  value={giftCards}
                  onChange={(e) => setGiftCards(Math.max(0, parseInt(e.target.value || "0", 10)))}
                  className="px-4 py-3 text-lg bebas tracking-wider focus:outline-none"
                />
                <div className="px-4 py-3 bg-[#f5f3f0] text-xs tracking-widest uppercase text-[#888880] flex items-center border-l border-black">
                  / {lang === "fr" ? "an" : "yr"}
                </div>
              </div>
            </label>

            {/* Videos input */}
            <label className="block mb-8">
              <div className="text-[10px] md:text-xs font-semibold tracking-[0.18em] uppercase text-[#0f0f0f] mb-3">
                {lang === "fr" ? "Vidéos éditées / an" : "Edited videos / year"}
              </div>
              <div className="grid grid-cols-[1fr_auto] border-[1.5px] border-black">
                <input
                  type="number"
                  min="0"
                  value={videos}
                  onChange={(e) => setVideos(Math.max(0, parseInt(e.target.value || "0", 10)))}
                  className="px-4 py-3 text-lg bebas tracking-wider focus:outline-none"
                />
                <div className="px-4 py-3 bg-[#f5f3f0] text-xs tracking-widest uppercase text-[#888880] flex items-center border-l border-black">
                  {lang === "fr" ? "vidéos" : "videos"}
                </div>
              </div>
            </label>

            {/* Months toggle */}
            <div>
              <div className="text-[10px] md:text-xs font-semibold tracking-[0.18em] uppercase text-[#0f0f0f] mb-1">
                {lang === "fr" ? "Mois d'opération" : "Months in operation"}
              </div>
              <div className="text-xs text-[#888880] font-light mb-4">
                {lang === "fr"
                  ? "Cliquez pour sélectionner les mois actifs de votre saison"
                  : "Click to select your operational months"}
              </div>
              <div className="grid grid-cols-6 gap-2">
                {monthNames.map((m, i) => {
                  const active = activeMonths.includes(i);
                  return (
                    <button
                      key={m}
                      onClick={() => toggleMonth(i)}
                      className={`px-2 py-3 text-[11px] md:text-xs font-bold tracking-widest uppercase border-[1.5px] transition-all ${
                        active
                          ? "bg-black text-white border-black"
                          : "bg-white text-black border-[#d4d2ce] hover:border-black"
                      }`}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
              <div className="text-xs text-[#888880] mt-3">
                {lang === "fr" ? "Saison" : "Season"} : {seasonLabel}
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="p-8 md:p-12 bg-[#f5f3f0]">
            <div className="text-[10px] md:text-xs font-semibold tracking-[0.18em] uppercase text-[#888880] mb-6">
              {lang === "fr" ? "Estimation annuelle" : "Annual estimate"}
            </div>

            <div className="flex flex-col gap-3 mb-6">
              <div className="bg-white border border-[#e8e6e2] p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-semibold text-base">{lang === "fr" ? "Réservations" : "Bookings"}</div>
                    <div className="text-xs text-[#888880] mt-1">
                      {fmtNum(bookings)} × $1.50
                    </div>
                  </div>
                  <div className="bebas text-xl tracking-wide">{fmt(sim.netBookings)}</div>
                </div>
                {sim.discount > 0 && (
                  <div className="mt-3 flex items-center justify-between gap-4 bg-red-50 border border-red-100 px-3 py-2">
                    <div className="text-xs md:text-sm text-red-600">
                      {lang === "fr"
                        ? `Rabais progressif appliqué (moy. ${sim.avgDiscountPct}%)`
                        : `Progressive discount applied (avg. ${sim.avgDiscountPct}%)`}
                    </div>
                    <div className="text-sm font-semibold text-red-600 whitespace-nowrap">
                      − {fmt(sim.discount)}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white border border-[#e8e6e2] p-4 flex items-start justify-between gap-4">
                <div>
                  <div className="font-semibold text-base">Gift Cards</div>
                  <div className="text-xs text-[#888880] mt-1">
                    {fmt(giftCards)} × 1.5%
                  </div>
                </div>
                <div className="bebas text-xl tracking-wide">{fmt(sim.giftSubtotal)}</div>
              </div>

              <div className="bg-white border border-[#e8e6e2] p-4 flex items-start justify-between gap-4">
                <div>
                  <div className="font-semibold text-base">{lang === "fr" ? "Montage Vidéo" : "Video Editing"}</div>
                  <div className="text-xs text-[#888880] mt-1">
                    {fmtNum(videos)} × $7.00
                  </div>
                </div>
                <div className="bebas text-xl tracking-wide">{fmt(sim.videoSubtotal)}</div>
              </div>
            </div>

            <div className="border-t-[1.5px] border-black pt-5">
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <div className="text-[10px] md:text-xs font-semibold tracking-widest uppercase text-[#888880] mb-1">
                    {lang === "fr" ? "Moy. / mois actif" : "Avg. / active month"}
                  </div>
                  <div className="bebas text-2xl md:text-3xl">{fmt(sim.avgPerMonth)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] md:text-xs font-semibold tracking-widest uppercase text-[#888880] mb-1">
                    {lang === "fr" ? "Mois de saison" : "Season months"}
                  </div>
                  <div className="bebas text-2xl md:text-3xl">
                    {sim.monthCount} {lang === "fr" ? "MOIS" : sim.monthCount === 1 ? "MO" : "MOS"}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <div className="text-[10px] md:text-xs font-semibold tracking-widest uppercase text-[#888880] mb-1">
                    {lang === "fr" ? "Total / an" : "Total / year"}
                  </div>
                  <div className="bebas text-3xl md:text-4xl text-red-600">{fmt(sim.total)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] md:text-xs font-semibold tracking-widest uppercase text-[#888880] mb-1">
                    {lang === "fr" ? "Économies vs plein tarif" : "Savings vs full price"}
                  </div>
                  <div className="bebas text-2xl md:text-3xl text-green-600">
                    − {fmt(sim.discount)}
                  </div>
                </div>
              </div>

              {sim.remainingForMax > 0 && (
                <div className="bg-black text-white px-4 py-3 flex items-center gap-3 text-xs md:text-sm mb-4">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-600 flex-shrink-0" />
                  <div>
                    {lang === "fr"
                      ? `Encore ${fmtNum(sim.remainingForMax)} réservations pour le rabais maximum de −40%`
                      : `${fmtNum(sim.remainingForMax)} more bookings to unlock the maximum −40% discount`}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#888880] mb-2">
                <span>$1.50 / booking</span>
                <span>1.5% gift cards</span>
                <span>$7.00 / {lang === "fr" ? "vidéo" : "video"}</span>
                <span>{lang === "fr" ? "Rabais progressif 0–40%" : "Progressive discount 0–40%"}</span>
              </div>
              <div className="text-xs text-[#888880] italic">
                {lang === "fr"
                  ? "* Tous les prix sont en dollars américains (USD)"
                  : "* All prices are in US dollars (USD)"}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black grid grid-cols-1 lg:grid-cols-2 border-b border-black">
        <div className="p-10 md:p-14 border-b lg:border-b-0 lg:border-r border-white/15 flex flex-col justify-center gap-4">
          <div className="text-red-600 text-xs font-semibold tracking-widest uppercase">
            {lang === "fr" ? "PASSEZ À L'ACTION" : "GET STARTED"}
          </div>
          <div className="bebas text-3xl md:text-4xl leading-none text-white">
            {lang === "fr" ? (
              <>PLANIFIEZ<br /><span className="text-red-600">UN APPEL</span></>
            ) : (
              <>SCHEDULE<br /><span className="text-red-600">A CALL</span></>
            )}
          </div>
        </div>
        <div className="p-10 md:p-14 flex flex-col justify-center gap-6">
          <p className="text-sm font-light text-white/60 leading-relaxed max-w-md">
            {lang === "fr"
              ? "Découvrez comment UDZM peut augmenter vos réservations, réduire les no-shows et simplifier vos opérations."
              : "See how UDZM could increase bookings, reduce no-shows, and streamline your operations."}
          </p>
          <div>
            <button
              type="button"
              onClick={() => setDemoOpen(true)}
              className="bg-red-600 hover:bg-red-700 text-white border-[1.5px] border-red-600 px-9 py-4 text-sm font-semibold tracking-widest uppercase transition-all">
              {lang === "fr" ? "Planifier un appel →" : "Schedule a call →"}
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="grid grid-cols-1 lg:grid-cols-2 border-t border-black">
        <div className="p-8 md:p-12 border-b lg:border-b-0 lg:border-r border-black flex flex-col gap-2">
          <img
            src="https://res.cloudinary.com/dxgvmldss/image/upload/v1773935934/Logo_UDZM_dr1kmx.png"
            alt="UDZM"
            loading="lazy"
            decoding="async"
            className="h-10 w-auto object-contain mb-2"
            style={{ maxWidth: 200 }}
          />
          <div className="text-sm font-semibold tracking-wide text-[#2a2a2a]">Ultimate DZ Management</div>
          <div className="text-xs font-light text-[#888880]">
            {lang === "fr"
              ? "Le système d'exploitation IA des dropzones modernes"
              : "The AI Operating System for Modern Dropzones"}
          </div>
        </div>
        <div className="p-8 md:p-12 flex items-center">
          <div className="text-xs text-[#888880] tracking-wide">
            © {new Date().getFullYear()} Ultimate DZ Management. All rights reserved.
          </div>
        </div>
      </footer>

      <DemoRequestDialog open={demoOpen} onOpenChange={setDemoOpen} lang={lang} />

      {/* Modal */}
      {modalOpen && currentModal && (
        <div
          className="fixed inset-0 bg-black/80 z-[2000] flex items-center justify-center p-4"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="bg-white border-2 border-black max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8 md:p-10 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setModalOpen(false)}
              className="absolute top-4 right-4 md:top-6 md:right-6 w-10 h-10 border-[1.5px] border-black flex items-center justify-center text-xl hover:bg-black hover:text-white transition-colors z-10"
            >
              ✕
            </button>

            <div className="flex items-start gap-5 mb-6">
              <div className="text-4xl md:text-5xl flex-shrink-0">{currentModal.icon}</div>
              <div>
                <div className="text-red-600 text-sm font-semibold tracking-widest">
                  {currentModal.num}
                </div>
                <h3 className="bebas text-2xl md:text-3xl mt-2 leading-none">
                  {currentModal.title[lang]}
                </h3>
                <div
                  className={`inline-block mt-4 px-5 py-1 text-xs font-bold tracking-[0.1em] uppercase ${currentModal.tagClass}`}
                >
                  {currentModal.tag[lang]}
                </div>
              </div>
            </div>

            <p className="text-sm md:text-base leading-relaxed text-[#2a2a2a] mb-6">
              {currentModal.desc[lang]}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[0, 1].map((i) => (
                <div
                  key={i}
                  className="aspect-[4/3] bg-[#f5f3f0] border-2 border-dashed border-[#d4d2ce] flex flex-col items-center justify-center gap-3 text-[#a8a6a2]"
                >
                  <svg
                    width="42"
                    height="42"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  <div className="text-sm font-light">
                    {lang === "fr" ? "Photos à venir" : "Photos coming soon"}
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default UDZMLanding;
